import { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '@/common/lib/api-client';
import { ONBOARDING_ROUTES } from '../onboardingRoutes';

const routeSteps = {
  '/choose-integration': 'CHOOSE_INTEGRATION',
  '/whatsapp-setup': 'SETUP_INTEGRATION',
  '/whatsapp-activation': 'ACTIVATE_INTEGRATION',
  '/whatsapp-activated': 'PAYMENT_PENDING',
  // Transient confirmation screen reached only via the Razorpay success
  // callback, before the step flips to COMPLETED — guard it the same as
  // the page it's launched from.
  '/payment-success': 'PAYMENT_PENDING',
};

export function OnboardingLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/v1/accounts/me/');
        const { user } = response.data;

        // Sync local storage state
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('company_id', user.company_id);
        localStorage.setItem('company_name', user.company_name);

        // No dedicated backend step exists for /company-registration — it
        // sits before CHOOSE_INTEGRATION. Gate it on company_id instead:
        // no company yet means still legitimately mid-registration.
        if (location.pathname === '/company-registration') {
          if (!user.company_id) {
            setLoading(false);
          } else {
            const targetRoute =
              ONBOARDING_ROUTES[user.onboarding_step] || '/choose-integration';
            navigate(targetRoute, { replace: true });
          }
          return;
        }

        const expectedStep = routeSteps[location.pathname];
        if (user.onboarding_step !== expectedStep) {
          // Unmatched/unrecognized step (e.g. company not registered yet)
          // must NOT fall back to /choose-integration here — that's this
          // block's own route for some callers, which would redirect to
          // itself and loop forever. /company-registration is the true
          // first step and is safe to land on repeatedly (it gates itself
          // on company_id, not on this step match).
          const targetRoute =
            ONBOARDING_ROUTES[user.onboarding_step] || '/company-registration';
          navigate(targetRoute, { replace: true });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error verifying onboarding step:', error);
        setLoading(false);
      }
    };

    checkStatus();
  }, [location.pathname, token, navigate]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="h-screen w-full bg-layer-1-page flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-t-primary border-r-transparent border-b-primary border-l-transparent animate-spin" />
          <p className="text-text-secondary text-sm tracking-wide">
            Checking onboarding step...
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
